import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_STARTS = [
    "My pool cage has a torn panel",
    "Do you serve my area?",
    "How do free estimates work?",
];

function getSessionId() {
    let id = sessionStorage.getItem("a1_assistant_session");
    if (!id) {
        id = window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `s-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        sessionStorage.setItem("a1_assistant_session", id);
    }
    return id;
}

function replaceLast(msgs, content) {
    const next = msgs.slice();
    next[next.length - 1] = { ...next[next.length - 1], content };
    return next;
}

export default function AssistantWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [restored, setRestored] = useState(false);
    const [handoffBusy, setHandoffBusy] = useState(false);
    const scrollRef = useRef(null);
    const navigate = useNavigate();

    const handoff = async () => {
        if (handoffBusy) return;
        setHandoffBusy(true);
        try {
            const res = await fetch(`${API}/api/assistant/handoff`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: getSessionId() }),
            });
            if (res.ok) {
                const data = await res.json();
                sessionStorage.setItem("a1_estimate_prefill", JSON.stringify(data.prefill || {}));
            }
        } catch { /* fall through to the plain form */ }
        setHandoffBusy(false);
        setOpen(false);
        navigate("/free-estimate");
    };

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    useEffect(() => {
        if (!open || restored) return;
        setRestored(true);
        fetch(`${API}/api/assistant/history/${getSessionId()}`)
            .then((r) => (r.ok ? r.json() : { messages: [] }))
            .then((d) => {
                if (d.messages?.length) {
                    setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
                }
            })
            .catch(() => {});
    }, [open, restored]);

    const send = async (text) => {
        const message = (text || input).trim();
        if (!message || busy) return;
        setInput("");
        setBusy(true);
        setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
        try {
            const res = await fetch(`${API}/api/assistant/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: getSessionId(), message }),
            });
            if (!res.ok) {
                let detail = "The assistant is unavailable right now. Please call 239-434-7306.";
                try {
                    detail = (await res.json()).detail || detail;
                } catch { /* keep default */ }
                setMessages((m) => replaceLast(m, detail));
                return;
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let acc = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    const data = line.slice(6);
                    if (data === "[DONE]") continue;
                    try {
                        const evt = JSON.parse(data);
                        if (evt.delta) {
                            acc += evt.delta;
                            setMessages((m) => replaceLast(m, acc));
                        }
                        if (evt.error) {
                            acc = evt.error;
                            setMessages((m) => replaceLast(m, acc));
                        }
                    } catch { /* partial SSE line */ }
                }
            }
            if (!acc) setMessages((m) => replaceLast(m, "Sorry — please try again, or call 239-434-7306."));
        } catch {
            setMessages((m) => replaceLast(m, "Connection problem. Please try again, or call 239-434-7306."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {open ? (
                <div className="assistant-panel" role="dialog" aria-label="A1 Screening assistant" data-testid="assistant-panel">
                    <div className="assistant-head">
                        <div>
                            <p className="assistant-title">A1 Screening Assistant</p>
                            <p className="assistant-sub">Pool cage &amp; screen repair questions</p>
                        </div>
                        <button type="button" className="assistant-close" onClick={() => setOpen(false)} aria-label="Close assistant" data-testid="assistant-close">
                            <X size={18} />
                        </button>
                    </div>
                    <p className="assistant-disclaimer">
                        General info only. Final recommendations and pricing are confirmed after technician review.
                    </p>
                    <div className="assistant-scroll" ref={scrollRef} data-testid="assistant-messages">
                        {messages.length === 0 ? (
                            <div className="assistant-empty">
                                <p>Hi! Tell us what's going on with your screens, or try one of these:</p>
                                {QUICK_STARTS.map((q) => (
                                    <button key={q} type="button" className="assistant-chip" onClick={() => send(q)} data-testid="assistant-quickstart">
                                        {q}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            messages.map((m, i) => (
                                <div key={i} className={`assistant-msg ${m.role}`} data-testid={`assistant-message-${m.role}`}>
                                    {m.content || <span className="assistant-typing">Thinking…</span>}
                                </div>
                            ))
                        )}
                    </div>
                    <div className="assistant-foot">
                        <input
                            className="assistant-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                            placeholder="Type your question…"
                            maxLength={1500}
                            aria-label="Ask the A1 Screening assistant"
                            data-testid="assistant-input"
                        />
                        <button type="button" className="assistant-send" onClick={() => send()} disabled={busy || !input.trim()} aria-label="Send message" data-testid="assistant-send">
                            <Send size={16} />
                        </button>
                    </div>
                    {messages.length > 0 ? (
                        <button
                            type="button"
                            className="assistant-handoff"
                            onClick={handoff}
                            disabled={handoffBusy}
                            data-testid="assistant-handoff-btn"
                        >
                            {handoffBusy ? "Preparing your form…" : "Request my free estimate"} <ArrowRight size={15} />
                        </button>
                    ) : null}
                    <p className="assistant-callout">
                        Prefer to talk? <a href="tel:+12394347306">Call 239-434-7306</a> · <Link to="/free-estimate">Free estimate</Link>
                    </p>
                </div>
            ) : null}
            <button
                type="button"
                className="assistant-launcher"
                onClick={() => setOpen((o) => !o)}
                aria-label={open ? "Close chat assistant" : "Chat with A1 Screening"}
                data-testid="assistant-launcher"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </button>
        </>
    );
}
